import React, { useEffect, useState, useContext, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Container } from "@mui/material";
import { styles } from "./styles/dataTableMD/dataTableMD.jsx";
import { useTheme } from "@mui/material/styles";
import DataGridHeader from "./components/dataTableMD/dataTableHeader.js";
import DataTableFilters from "./components/dataTableMD/dataTableFilters.js";
import DataGridFooter from "./components/dataTableMD/dataTableFooter.js";
import DataTable from "./components/dataTableMD/dataTable.js";
import { api } from "../../api/index";
import { LoadingIcon } from "./loadingIcon.js";
import { WebSocketContext } from "../context/websocketsContext.js";

// De-duplicate rows by `id`, keeping the LAST occurrence's data in the first-seen position.
// Guards against duplicate incident IDs when a websocket-pushed row later also arrives in a
// server page (`[...data, ...page]`), or when the same id spans paginated appends.
const dedupeById = (rows) => {
  if (!Array.isArray(rows)) return rows;
  const map = new Map();
  for (const row of rows) {
    const key = row && row.id !== undefined ? row.id : row;
    if (map.has(key)) {
      map.set(key, { ...map.get(key), ...row });
    } else {
      map.set(key, row);
    }
  }
  return Array.from(map.values());
};

// Whether a typed column filter value actually constrains anything (FEAT-031).
const isColumnFilterActive = (val, type) => {
  if (val === undefined || val === null) return false;
  if (type === "select") return Array.isArray(val) && val.length > 0;
  if (type === "date") {
    if (typeof val !== "object") return false;
    if (val.mode === "relative") return !!val.days;
    if (val.mode === "range") return !!(val.start || val.end);
    if (val.mode === "exact") return !!val.date;
    return false;
  }
  return typeof val === "string" && val.trim() !== "";
};

// Does a loaded row satisfy a typed column filter?
const rowMatchesColumnFilter = (row, field, val, type) => {
  const raw = row[field];
  if (type === "select") {
    const rowVals = String(raw === undefined || raw === null ? "" : raw)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    return val.some((sel) => rowVals.includes(sel));
  }
  if (type === "date") {
    if (!raw) return false;
    const t = new Date(raw).getTime();
    if (Number.isNaN(t)) return false;
    if (val.mode === "relative") return t >= Date.now() - val.days * 86400000;
    if (val.mode === "range") {
      const s = val.start ? new Date(val.start + "T00:00:00").getTime() : -Infinity;
      const e = val.end ? new Date(val.end + "T23:59:59").getTime() : Infinity;
      return t >= s && t <= e;
    }
    if (val.mode === "exact") {
      return new Date(raw).toDateString() === new Date(val.date + "T00:00:00").toDateString();
    }
    return true;
  }
  return String(raw === undefined || raw === null ? "" : raw)
    .toLowerCase()
    .includes(String(val).trim().toLowerCase());
};

const DataTableMD = ({
  title,
  dbTable,
  filters,
  toggles,
  columns,
  addButton,
  addSuccessMessage,
  sort,
  isTab,
  apiBody,
}) => {
  const navigate = useNavigate();
  const [data, setData] = useState([]); // accumulated rows across loaded server pages
  const [currentPageData, setCurrentPageData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filterData, setFilterData] = useState({});
  const [activeToggles, setActiveToggles] = useState(toggles);
  const [page, setPage] = useState(0); // 0-indexed

  // Default page size fits the viewport but never drops below 25 (FEAT-032).
  const computePageSize = () => {
    const computed = parseInt((window.innerHeight - 400) / 75, 10);
    return Math.max(25, Number.isNaN(computed) ? 25 : computed);
  };
  const [pageSize, setPageSize] = useState(computePageSize());
  const [pageSizes, setPageSizes] = useState(() =>
    Array.from(new Set([computePageSize(), 25, 50, 100, 200])).sort((a, b) => a - b)
  );
  // Per-column client-side sort + filter over the loaded rows (FEAT-031).
  const [sortConfig, setSortConfig] = useState(null);
  const [columnFilters, setColumnFilters] = useState({});
  const didMountResizeRef = useRef(false);
  const [totalPages, setTotalPages] = useState(1);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [subscribe, unsubscribe] = useContext(WebSocketContext);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  // Monotonic request id: any response whose id != the latest is ignored, so a stale
  // filter/pageSize change can't clobber newer results. loadingRef gives a synchronous guard.
  const reqIdRef = useRef(0);
  const loadingRef = useRef(false);
  // Server fetch size is decoupled from the display page size: pull rows in chunks of at least
  // FETCH_CHUNK (far fewer round-trips when paging deep) and slice locally to the page size.
  const FETCH_CHUNK = 100;

  const theme = useTheme();
  const style = styles(theme);

  // Build the request body from base apiBody + typed filterData. The All/Open/Closed status
  // toggle is translated to a real predicate: "Open" => status <> Closed (New/Open/Investigate/
  // Waiting/Blocked), "Closed" => status == Closed, "All" => no status predicate.
  const buildBody = (pageKey) => {
    let body = {};
    if (apiBody) body = JSON.parse(JSON.stringify(apiBody));
    body.pageSize = Math.max(FETCH_CHUNK, pageSize);
    if (pageKey) body.pageKey = pageKey;
    // Start from the page's BASE filters (e.g. Responders -> permission=responder) and MERGE the
    // top-level UI filters (district/school/status/assignee) onto them. The backend reads filters
    // via json.loads(body["filters"]), so `filters` MUST be sent as a JSON string — an array is
    // serialized by axios as bracket params (filters[0][key]=...) and never parsed, which silently
    // dropped the base filter and made the top-level district/school filters do nothing.
    const filterList = Array.isArray(body.filters) ? [...body.filters] : [];
    for (const field in filterData) {
      const value = filterData[field];
      if (field === "status") {
        const vals = Array.isArray(value) ? value : [value];
        if (vals.includes("Closed")) {
          filterList.push({ key: "status", value: "Closed", expression: "=" });
        } else if (vals.includes("Open")) {
          filterList.push({ key: "status", value: "Closed", expression: "<>" });
        }
      } else {
        filterList.push({ key: field, value, expression: "=" });
      }
    }
    if (filterList.length) body.filters = JSON.stringify(filterList);
    else delete body.filters;
    return body;
  };

  // Fetch the FIRST page for the current filters/pageSize (resets accumulation).
  const loadFirstPage = async () => {
    reqIdRef.current += 1;
    const myReq = reqIdRef.current;
    loadingRef.current = true;
    setLoading(true);
    setLoaded(false);
    try {
      const response = await api("get", buildBody(null), dbTable);
      if (myReq !== reqIdRef.current) return; // superseded by a newer query
      if (response.status === 200) {
        const rows = dedupeById(response.data.data || []);
        setData(rows);
        setLastEvaluatedKey(
          response.data.LastEvaluatedKey ? JSON.stringify(response.data.LastEvaluatedKey) : null
        );
        setTotalCount(
          response.data.TotalCount != null ? response.data.TotalCount : rows.length
        );
      }
    } catch (error) {
      // leave prior state; loaded still flips so the table renders
    } finally {
      if (myReq === reqIdRef.current) {
        loadingRef.current = false;
        setLoading(false);
        setLoaded(true);
      }
    }
  };

  // Append the NEXT server page to the accumulated rows (same query session).
  const loadMore = async () => {
    if (loadingRef.current || !lastEvaluatedKey) return;
    const myReq = reqIdRef.current;
    loadingRef.current = true;
    setLoading(true);
    try {
      const response = await api("get", buildBody(lastEvaluatedKey), dbTable);
      if (myReq !== reqIdRef.current) return;
      if (response.status === 200) {
        setData((prev) => dedupeById([...prev, ...(response.data.data || [])]));
        setLastEvaluatedKey(
          response.data.LastEvaluatedKey ? JSON.stringify(response.data.LastEvaluatedKey) : null
        );
        if (response.data.TotalCount != null) setTotalCount(response.data.TotalCount);
      }
    } catch (error) {
      // ignore; navigating again will retry
    } finally {
      if (myReq === reqIdRef.current) {
        loadingRef.current = false;
        setLoading(false);
      }
    }
  };

  // Initial load.
  useEffect(() => {
    loadFirstPage();
    // eslint-disable-next-line
  }, []);

  // Re-query from scratch when the top-level filters change (district/school/status/assignee).
  useEffect(() => {
    if (!loaded) return; // the mount effect already performs the first load
    setPage(0);
    loadFirstPage();
    // eslint-disable-next-line
  }, [filterData]);

  // Forward-fill: load more server pages until enough rows are accumulated to render the
  // current page. Column filters page over the already-loaded set, so skip fill then.
  const hasColumnFilters = Object.keys(columnFilters).some((f) =>
    isColumnFilterActive(columnFilters[f], (columns || []).find((c) => c.field === f)?.filterType || "text")
  );
  // A per-column search/filter OR a sort must operate over the FULL data set, not just the rows
  // loaded so far. Otherwise searching an incident id (e.g. 801) returns nothing and sorting
  // desc only reflects the first pages (highest shown was 143). So when either is active, keep
  // pulling every remaining server page until fully loaded. With neither active, only load
  // enough pages to render the current page (progressive pagination).
  const needsFullData = hasColumnFilters || Boolean(sortConfig && sortConfig.field);
  useEffect(() => {
    if (loadingRef.current || !lastEvaluatedKey) return;
    if (needsFullData) {
      // Each loadMore appends a page + updates lastEvaluatedKey, re-running this effect until
      // the whole set is loaded (lastEvaluatedKey === null).
      loadMore();
      return;
    }
    const needed = (page + 1) * pageSize;
    if (data.length < needed) loadMore();
    // eslint-disable-next-line
  }, [page, pageSize, data, lastEvaluatedKey, needsFullData]);

  useEffect(() => {
    subscribe(dbTable, (messageData) => {
      if (messageData.command === "newIncident") {
        const incidentData = messageData.incident;
        setData((prev) => {
          if (prev.some((r) => r.id === incidentData.id)) return prev;
          return dedupeById([...prev, incidentData]);
        });
      } else if (messageData.command === "incidentUpdated") {
        const incidentData = messageData.incident;
        setData((prev) => {
          const idx = prev.findIndex((r) => r.id === incidentData.id);
          if (idx === -1) return prev;
          const next = [...prev];
          next[idx] = { ...next[idx], ...incidentData };
          return next;
        });
      }
    });
    return () => {
      unsubscribe(dbTable);
    };
    // eslint-disable-next-line
  }, [subscribe, unsubscribe, dbTable]);

  const processFilter = async (field, values) => {
    if (!field || (!values && values !== null)) return;
    const tempFilterData = { ...filterData };
    if (!Array.isArray(values)) {
      values = values ? [values] : [];
    }
    if (activeToggles && activeToggles.field === field) {
      setActiveToggles({ ...activeToggles, active: values });
    }
    if (values.includes("All") || values.length === 0) {
      // Clearing a filter also clears any dependent filters (e.g. school depends on district).
      for (let filter in filters) {
        if (filters[filter].apiConfig && filters[filter].apiConfig.requires) {
          if (filters[filter].apiConfig.requires.includes(field)) {
            delete tempFilterData[filters[filter].field];
          }
        }
      }
      delete tempFilterData[field];
    } else {
      tempFilterData[field] = values;
    }
    setFilterData(tempFilterData);
  };

  const handleRowClick = async (id) => {
    const formattedTitle = title.replace(/s$/, "").replace(" ", "");
    const lowercaseTitle = formattedTitle.toLowerCase();
    const thisRecord = data.filter((record) => record.id === id);
    navigate(`/${lowercaseTitle}/${id}`, { state: thisRecord[0] });
  };

  // Cycle a column's sort: none -> asc -> desc -> none. Client-side over loaded rows.
  const handleSort = (field) => {
    setSortConfig((prev) => {
      if (!prev || prev.field !== field) return { field, dir: "asc" };
      if (prev.dir === "asc") return { field, dir: "desc" };
      return null;
    });
    setPage(0);
  };

  const handleColumnFilter = (field, value) => {
    setColumnFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const fieldTypeMap = useMemo(() => {
    const m = {};
    (columns || []).forEach((c) => {
      m[c.field] = c.filterType || "text";
    });
    return m;
  }, [columns]);

  const filterOptionsMap = useMemo(() => {
    const m = {};
    (columns || []).forEach((c) => {
      if (c.filterType === "select") {
        const set = new Set();
        data.forEach((row) => {
          const raw = row[c.field];
          if (raw === undefined || raw === null || raw === "") return;
          String(raw).split(",").map((s) => s.trim()).filter(Boolean).forEach((v) => set.add(v));
        });
        m[c.field] = Array.from(set).sort();
      }
    });
    return m;
  }, [columns, data]);

  // Filtered + sorted view of the loaded rows (client-side, FEAT-031).
  const derivedData = useMemo(() => {
    let out = data;
    const activeCols = Object.keys(columnFilters).filter((f) =>
      isColumnFilterActive(columnFilters[f], fieldTypeMap[f] || "text")
    );
    if (activeCols.length) {
      out = out.filter((row) =>
        activeCols.every((f) => rowMatchesColumnFilter(row, f, columnFilters[f], fieldTypeMap[f] || "text"))
      );
    }
    if (sortConfig && sortConfig.field) {
      const { field, dir } = sortConfig;
      out = [...out].sort((a, b) => {
        const av = a[field];
        const bv = b[field];
        const an = Number(av);
        const bn = Number(bv);
        let cmp;
        if (!Number.isNaN(an) && !Number.isNaN(bn) && av !== "" && av !== null && bv !== "" && bv !== null) {
          cmp = an - bn;
        } else {
          cmp = String(av === undefined || av === null ? "" : av).localeCompare(
            String(bv === undefined || bv === null ? "" : bv)
          );
        }
        return dir === "desc" ? -cmp : cmp;
      });
    }
    return out;
  }, [data, columnFilters, sortConfig, fieldTypeMap]);

  // Resize listener (debounced) — recompute the viewport-based page size.
  useEffect(() => {
    let resizeTimer;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        setWindowSize({ width: window.innerWidth, height: window.innerHeight });
      }, 250);
    };
    window.addEventListener("resize", onResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  useEffect(() => {
    if (!didMountResizeRef.current) {
      didMountResizeRef.current = true;
      return;
    }
    const computed = Math.max(25, parseInt((windowSize.height - 400) / 75, 10) || 25);
    setPageSize((prev) => (prev === computed ? prev : computed));
    setPageSizes(Array.from(new Set([computed, 25, 50, 100, 200])).sort((a, b) => a - b));
    setPage(0);
  }, [windowSize]);

  // Total page count + the current page slice. When there are no column filters the count comes
  // from the server TotalCount (so all pages are reachable even before every page is loaded);
  // with column filters we page over the filtered loaded set.
  useEffect(() => {
    const base = hasColumnFilters ? derivedData.length : Math.max(totalCount, data.length);
    setTotalPages(Math.max(1, Math.ceil(base / pageSize)));
    const start = page * pageSize;
    setCurrentPageData(derivedData.slice(start, start + pageSize));
    // eslint-disable-next-line
  }, [derivedData, page, pageSize, totalCount, hasColumnFilters, data.length]);

  return (
    <Container sx={style.dataGridStyleContainer}>
      <DataGridHeader
        title={title}
        dbTable={dbTable}
        toggles={activeToggles}
        processFilter={processFilter}
        addButton={addButton}
        columns={columns}
        addSuccessMessage={addSuccessMessage}
        allData={data}
        setAllData={setData}
        isTab={isTab}
      />

      {filters?.length > 0 && (
        <DataTableFilters filters={filters} processFilter={processFilter} />
      )}
      {loaded ? (
        <DataTable
          data={currentPageData}
          fields={columns}
          handleRowClick={handleRowClick}
          sortConfig={sortConfig}
          onSort={handleSort}
          columnFilters={columnFilters}
          onColumnFilter={handleColumnFilter}
          filterOptionsMap={filterOptionsMap}
        />
      ) : (
        <Container sx={style.dataTableLoadingContainer}>
          <LoadingIcon page="dataTable" />
        </Container>
      )}
      <DataGridFooter
        page={page}
        pageSize={pageSize}
        pageSizes={pageSizes}
        setPage={setPage}
        setPageSize={setPageSize}
        totalPages={totalPages}
        totalRecords={totalCount}
        loadedRecords={data.length}
        title={title}
      />
    </Container>
  );
};

export default DataTableMD;
